import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { API_CONFIG, FEATURE_FLAGS } from '../config/app.config';

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip retry if feature is disabled
    if (!FEATURE_FLAGS.enableRetry) {
      return next.handle(req);
    }

    // Skip retry for non-retryable methods
    if (!this.isRetryableMethod(req.method)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      retry({
        count: API_CONFIG.retry.maxAttempts,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          if (!this.isRetryableError(error)) {
            throw error;
          }

          const delay =
            API_CONFIG.retry.delay *
            Math.pow(API_CONFIG.retry.backoffMultiplier, retryCount - 1);

          if (FEATURE_FLAGS.enableInterceptorLogging) {
            console.log(
              `[RetryInterceptor] Retrying request (attempt ${retryCount}/${API_CONFIG.retry.maxAttempts}) after ${delay}ms`,
              {
                url: req.url,
                status: error.status,
                delay,
              }
            );
          }

          return timer(delay);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (FEATURE_FLAGS.enableErrorLogging) {
          console.error('[RetryInterceptor] Request failed after retries', {
            url: req.url,
            status: error.status,
            message: error.message,
          });
        }
        return throwError(() => error);
      })
    );
  }

  private isRetryableMethod(method: string): boolean {
    const upperMethod = method.toUpperCase();
    return API_CONFIG.retry.retryableMethods.includes(upperMethod as any);
  }

  private isRetryableError(error: HttpErrorResponse): boolean {
    return API_CONFIG.retry.retryableStatuses.includes(error.status as any);
  }
}
